import { defineStore } from 'pinia';
import { ContentBlock } from '@/modules/content-blocks/interfaces';
import { getBlock } from '@/core/block-registry';
import { mapContentBlocks } from '@/core/collections/job-boards/job-board.helper';
import type { JobBoard } from '@/core/collections/job-boards/job-board.interface';
import { clone } from '@/core/utils';

export const useLiveEditorStore = defineStore('liveEditor', {
   state: () => ({
      blocks: [] as ContentBlock[],
      editingBlockId: null as string | null,
      draftData: null as Record<string, any> | null, // deep copy, will only overwrite block data "on save"
      original: [] as ContentBlock[],
      isDirty: false,
      isSavingChanges: false,
   }),

   getters: {
      editingBlock: (state): ContentBlock | null => {
         if (!state.editingBlockId) return null;
         return state.blocks.find((block) => block.id === state.editingBlockId) ?? null;
      },

      editingBlockDefinition(): ReturnType<typeof getBlock> | null {
         if (!this.editingBlock) return null;
         return getBlock(this.editingBlock.type);
      },
   },

   actions: {
      setBlocks(contentBlocks: ContentBlock[]) {
         this.blocks = contentBlocks;
         this.original = clone(contentBlocks);
      },

      initializeBlocks(jobBoard: JobBoard) {
         this.setBlocks(mapContentBlocks(jobBoard));
      },

      removeBlock(blockId: string) {
         const index = this.blocks.findIndex((b) => b.id === blockId);
         if (index !== -1) this.blocks.splice(index, 1);
         this.discardEditing();
      },

      startEditing(blockId: string) {
         const block = this.blocks.find((b) => b.id === blockId);
         if (!block) return;
         this.editingBlockId = blockId;
         this.draftData = clone(block.data); // isolated draft copy for editing
      },

      saveEditing() {
         if (!this.editingBlockId || !this.draftData) return;
         const block = this.blocks.find((b) => b.id === this.editingBlockId);
         if (block) block.data = this.draftData;
         this.discardEditing();
      },

      discardEditing() {
         this.editingBlockId = null;
         this.draftData = null;
      },
   },
});
